import { useState, useCallback } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, CopyButton } from '@devkit/ui';
import { Hash } from 'lucide-react';
import { generateHash, SUPPORTED_HASH_ALGORITHMS, type HashAlgorithm } from '@devkit/tools-security';
import { trackToolExecute, trackCopy, trackClear } from '@devkit/analytics';

export default function HashGeneratorPage() {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState<Record<HashAlgorithm, string> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [computing, setComputing] = useState(false);

  const handleHash = useCallback(async () => {
    setError(null);
    setComputing(true);
    try {
      const results = await Promise.all(
        SUPPORTED_HASH_ALGORITHMS.map(async (alg) => {
          const r = await generateHash(input, alg);
          return [alg, r.data ?? ''] as const;
        })
      );
      const map: Record<string, string> = {};
      for (const [alg, hash] of results) map[alg] = hash;
      setOutput(map as Record<HashAlgorithm, string>);
      trackToolExecute('hash-generator');
    } catch (e) {
      setError((e as Error).message);
      setOutput(null);
    } finally {
      setComputing(false);
    }
  }, [input]);

  const clearAll = useCallback(() => {
    setInput('');
    setOutput(null);
    setError(null);
    trackClear('hash-generator');
  }, []);

  return (
    <ToolLayout slug="hash-generator">
      <Card>
        <CardContent className="p-6">
          <div className="mb-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <p className="text-sm text-amber-300">
              <strong>Note:</strong> MD5 and SHA-1 are cryptographically broken — only use them for non-security
              purposes (checksums, fingerprinting). For security-sensitive work, use SHA-256 or higher.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Input Text
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Enter text to hash..."
              className="w-full h-32 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
            />
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <Button onClick={handleHash} disabled={!input || computing} className="gap-2">
              <Hash className="w-4 h-4" />
              {computing ? 'Computing...' : 'Generate Hashes'}
            </Button>
            <Button variant="ghost" onClick={clearAll}>
              Clear
            </Button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {output && (
            <div className="space-y-3">
              {SUPPORTED_HASH_ALGORITHMS.map((alg) => (
                <div
                  key={alg}
                  className="bg-surface/40 border border-slate-700/50 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white">{alg}</span>
                      <span className="text-xs text-slate-500">
                        ({output[alg].length * 4} bits · {output[alg].length / 2} bytes)
                      </span>
                    </div>
                    <CopyButton
                      text={output[alg]}
                      onCopy={() => trackCopy('hash-generator')}
                    />
                  </div>
                  <code className="block text-xs font-mono text-green-300 break-all bg-slate-900/50 p-3 rounded">
                    {output[alg]}
                  </code>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </ToolLayout>
  );
}
